/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Component imports
import { Avatar, Button, Card, Checkbox, Divider, Dropdown, Empty, Input, Space, Tree, Typography, Tooltip } from 'antd';
import { LoadingOutlined, SearchOutlined, SmileOutlined } from '@ant-design/icons';
import { ArrowDownIcon } from '../../Icons/ArrowDownIcon';
import { FilterIcon } from '../../Icons/FilterIcon';
import TruncatedTextWithShowMore from '../../UI/TruncatedTextWithShowMore';

const AgentsPicker = (props) => {
  const {
    className,
    disabled,
    loading,
    value,
    onChange,
    agentsList,
    showDeployments = true,
    onAfterChange
  } = props;

  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [displayCount, setDisplayCount] = useState(0);

  const filteredAgentsList = searchTerm ? agentsList?.filter(agent =>
    agent.displayName.toLowerCase().includes(searchTerm.toLowerCase())
      || (showDeployments && agent.botDeployments?.some(dep =>
        dep.displayName.toLowerCase().includes(searchTerm.toLowerCase())
      ))
  ) : agentsList;

  const triggerChange = (changedValue) => {
    if (typeof onChange === 'function') {
      onChange(changedValue);

      if (typeof onAfterChange === 'function') {
        onAfterChange(changedValue);
      }
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Determine if "All" is selected when showing only agents
  const isAllSelected = showDeployments
    ? (!value || value.length === 0) // In deployments mode, empty selection means "all"
    : (!value || value.length === 0 || value.length >= agentsList?.length);

  // Get all possible keys (for checking if everything is selected)
  const getAllPossibleKeys = () => {
    const keys = [];
    agentsList?.forEach(bot => {
      keys.push(bot.id);
      if (showDeployments && bot.botDeployments?.length > 0) {
        bot.botDeployments.forEach(dep => {
          keys.push(`${bot.id}-${dep.id}`);
        });
        keys.push(`${bot.id}-includeOtherDeployments`);
      }
    });
    return keys;
  };

  // Determine checked keys for the Tree
  const getCheckedKeys = () => {
    if (!showDeployments) {
      return isAllSelected ? [] : value || [];
    }

    return value || [];
  };

  const generateTreeData = () => {
    if (!showDeployments) {
      return filteredAgentsList?.map(bot => ({
        key: bot.id,
        title: (
          <Space wrap={false} align="center">
            <Avatar src={bot.image} size={20}>
              <SmileOutlined className="d-inline v-align-baseline" />
            </Avatar>
            <TruncatedTextWithShowMore showMore={false} enableTooltip={true} text={bot.displayName} linesToClamp={1} />
          </Space>
        )
      })) || [];
    }

    return filteredAgentsList?.map(bot => ({
      key: bot.id,
      title: (
        <Space wrap={false} align="center">
          <Avatar src={bot.image} size={20}>
            <SmileOutlined className="d-inline v-align-baseline" />
          </Avatar>
          <TruncatedTextWithShowMore showMore={false} enableTooltip={true} text={bot.displayName} linesToClamp={1} />
        </Space>
      ),
      selectable: false,
      children: [
        ...((bot.botDeployments || []).map(deployment => {
          return {
            title: <TruncatedTextWithShowMore showMore={false} enableTooltip={true} text={deployment.displayName} linesToClamp={1} />,
            key: `${bot.id}-${deployment.id}`,
            selectable: false
          };
        })),
        {
          title: (
            <Tooltip
              title={t('Includes analytics from deleted and/or prior deployments.')}
              placement="right"
              mouseEnterDelay={0.5}
            >
              {t('Other Deployments')}
            </Tooltip>
          ),
          key: `${bot.id}-includeOtherDeployments`,
          selectable: false
        }
      ]
    })) || [];
  };

  const handleTreeCheck = (checkedKeys) => {
    if (!showDeployments) {
      // Simple agent selection logic (as before)
      if (checkedKeys.length === filteredAgentsList?.length && filteredAgentsList?.length === agentsList?.length) {
        triggerChange([]);
      } else {
        const filteredIds = filteredAgentsList?.map(agent => agent.id) || [];
        const preservedSelections = (value || []).filter(id => !filteredIds.includes(id));
        triggerChange([...preservedSelections, ...checkedKeys]);
      }
    } else {
      // Complex deployment selection logic
      const allPossibleKeys = getAllPossibleKeys();

      // If all keys are selected, clear the selection (equivalent to "All" selected)
      if (checkedKeys.length === allPossibleKeys.length) {
        triggerChange([]);
      } else {
        // Get visible item keys to determine which items were affected by this selection
        const visibleKeys = [];
        filteredAgentsList?.forEach(bot => {
          visibleKeys.push(bot.id);
          if (bot.botDeployments?.length > 0) {
            bot.botDeployments.forEach(dep => {
              visibleKeys.push(`${bot.id}-${dep.id}`);
            });
            visibleKeys.push(`${bot.id}-includeOtherDeployments`);
          }
        });

        // Preserve selections that aren't currently visible
        const preservedSelections = (value || []).filter(id => !visibleKeys.includes(id));

        triggerChange([...preservedSelections, ...checkedKeys]);
      }
    }
  };

  useEffect(() => {
    // Update display count whenever agentsList or value changes
    let count = 0;
    if (showDeployments) {
      // If value is empty when showDeployments is true, it means "all selected"
      if (!value || value.length === 0) {
        count = agentsList?.length || 0;
      } else {
        // Show the count of agents that have at least one item selected
        const selectedAgents = new Set();
        value?.forEach(key => {
          const [agentId] = key.split('-');
          selectedAgents.add(agentId);
        });
        count = selectedAgents.size;
      }
    } else {
      count = value?.length || agentsList?.length || 0;
    }

    setDisplayCount(count);
  }, [value, agentsList, showDeployments]);

  const checkedKeys = getCheckedKeys();
  const treeData = generateTreeData();

  return (
    <Dropdown
      disabled={disabled}
      loading={loading}
      trigger="click"
      overlayClassName="w-100"
      overlayStyle={{ maxWidth: 320 }}
      overlay={
        <Card
          size="small"
          className="overflow-y-auto overflow-x-hidden p-0"
          bodyStyle={{ maxHeight: 340 }}
        >
          <Space className="p-2" direction="vertical" style={{ width: '100%' }}>
            <Input
              placeholder={t('Search agents')}
              prefix={<SearchOutlined className="text-muted" />}
              value={searchTerm}
              onChange={handleSearchChange}
              allowClear
              size="large"
            />

            <Checkbox
              className="p-1"
              checked={isAllSelected}
              onChange={() => triggerChange([])}
            >
              <Typography.Text strong>
                {t('All Workspace Agents')} ({agentsList?.length})
              </Typography.Text>
            </Checkbox>
          </Space>

          <Divider className="my-0" />

          {
            filteredAgentsList?.length > 0 ? (
              <Tree
                className="px-3 py-2 ant-tree-no-indent"
                checkable
                selectable={false}
                checkedKeys={checkedKeys}
                treeData={treeData}
                onCheck={handleTreeCheck}
                switcherIcon={showDeployments ? <ArrowDownIcon /> : null}
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t('No agents found')}
              />
            )
          }
        </Card>
      }
    >
      <Button size="large" className={className}>
        <Space>
          <FilterIcon />
          {t('Agents')}
          {loading ? <LoadingOutlined className="fs-sm" /> : displayCount > 0 ? `(${displayCount})` : null}
          <ArrowDownIcon />
        </Space>
      </Button>
    </Dropdown>
  );
};

AgentsPicker.propTypes = {
  className: PropTypes.string,
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
  onAfterChange: PropTypes.func,
  agentsList: PropTypes.array,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  showDeployments: PropTypes.bool
};

export default AgentsPicker;
